import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../services/api';
import MapPicker from '../components/MapPicker';
import NotificationSettings from '../components/NotificationSettings';
import { PageHead, PagePanel } from '../components/appPage';
import './Profile.css';

const Profile = () => {
  const { t, i18n } = useTranslation();
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    address: user?.address || '',
    addressLocation:
      user?.addressLatitude != null && user?.addressLongitude != null
        ? { lat: Number(user.addressLatitude), lng: Number(user.addressLongitude) }
        : null
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);

    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        address: formData.address,
        addressLatitude: formData.addressLocation?.lat ?? null,
        addressLongitude: formData.addressLocation?.lng ?? null
      };
      const response = await usersAPI.updateProfile(payload);
      updateUser(response.data.user);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };

  return (
    <div className="profile-page page-shell">
      <PageHead
        title={t('profile.title')}
        lead={t('profile.lead', 'Управлявайте личните данни и предпочитанията за език.')}
      />

      {success && (
        <div className="success-message profile-success-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {t('profile.updateSuccess')}
        </div>
      )}

      <PagePanel title={t('profile.personalInfo')} titleId="profile-personal-heading">
        <form onSubmit={handleSubmit} className="profile-form">
          <div className="page-field">
            <label htmlFor="firstName">{t('auth.firstName')}</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="page-field">
            <label htmlFor="lastName">{t('auth.lastName')}</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="page-field">
            <label htmlFor="phone">{t('auth.phone')}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+359..."
            />
          </div>

          <div className="page-field">
            <label htmlFor="address">{t('profile.address')}</label>
            <MapPicker
              label={t('profile.addressMapLabel', 'Изберете адрес от картата')}
              value={formData.addressLocation}
              onChange={(coords) =>
                setFormData((prev) => ({ ...prev, addressLocation: coords }))
              }
              addressValue={formData.address}
              onAddressChange={(address) =>
                setFormData((prev) => ({ ...prev, address }))
              }
              helperText={t(
                'profile.addressMapHint',
                'Този адрес се използва за намиране на карпули във вашия район.'
              )}
            />
          </div>

          <div className="profile-form__actions">
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      </PagePanel>

      <PagePanel title={t('profile.settings')} titleId="profile-settings-heading">
        <div className="profile-setting">
          <span className="profile-setting__label">{t('profile.language')}</span>
          <div className="profile-segment" role="group" aria-label={t('profile.language')}>
            <button
              type="button"
              className={i18n.language === 'bg' ? 'is-active' : ''}
              onClick={() => handleLanguageChange('bg')}
            >
              Български
            </button>
            <button
              type="button"
              className={i18n.language === 'en' ? 'is-active' : ''}
              onClick={() => handleLanguageChange('en')}
            >
              English
            </button>
          </div>
        </div>

        <div className="profile-setting profile-setting--last">
          <span className="profile-setting__label">{t('profile.subscription')}</span>
          <p className="profile-setting__value">
            {user?.isPremium ? (
              <span className="premium-badge">{t('profile.premium', 'Premium')}</span>
            ) : (
              <span className="profile-free-label">{t('profile.free')}</span>
            )}
          </p>
        </div>
      </PagePanel>

      <PagePanel title={t('notifications.title')} titleId="profile-notifications-heading">
        <NotificationSettings isPremium={user?.isPremium} />
      </PagePanel>
    </div>
  );
};

export default Profile;
